import { Eye, EyeOff } from "lucide-react";
import { type ReactNode, useState } from "react";

import { cn } from "@/lib/utils";

import { InlineError } from "./inline-error";

export const PasswordField = ({
  autoComplete,
  error,
  hideMessage = false,
  id,
  label,
  onChange,
  onRevealedChange,
  revealed,
  value,
  aside,
}: {
  aside?: ReactNode;
  autoComplete: string;
  error?: string | null;
  hideMessage?: boolean;
  id: string;
  label: string;
  onChange: (value: string) => void;
  onRevealedChange?: (revealed: boolean) => void;
  revealed?: boolean;
  value: string;
}) => {
  const [localRevealed, setLocalRevealed] = useState(false);
  const visible = revealed ?? localRevealed;
  const errorId = error ? `${id}-error` : undefined;
  const toggle = () => {
    const next = !visible;
    if (onRevealedChange) {
      onRevealedChange(next);
      return;
    }
    setLocalRevealed(next);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-4">
        <label className="font-medium text-sm" htmlFor={id}>
          {label}
        </label>
        {aside}
      </div>
      <div className="relative flex">
        <input
          aria-describedby={errorId}
          aria-invalid={error ? true : undefined}
          autoComplete={autoComplete}
          className={cn(
            "auth-focus box-border h-[52px] min-w-0 flex-1 rounded-[10px] border bg-white pr-14 pl-4 text-base text-navy-900",
            error ? "border-error" : "border-n-600"
          )}
          id={id}
          onChange={(event) => onChange(event.target.value)}
          type={visible ? "text" : "password"}
          value={value}
        />
        <button
          aria-label={visible ? "Hide password" : "Show password"}
          aria-pressed={visible}
          className="auth-focus absolute top-1 right-1 flex size-11 items-center justify-center rounded-lg border-0 bg-transparent text-n-600"
          onClick={toggle}
          type="button"
        >
          {visible ? (
            <EyeOff aria-hidden size={20} strokeWidth={1.75} />
          ) : (
            <Eye aria-hidden size={20} strokeWidth={1.75} />
          )}
        </button>
      </div>
      {hideMessage ? null : (
        <InlineError id={errorId} message={error ?? null} />
      )}
    </div>
  );
};
